// Экран 10 (веб W09) · Лимиты и списания. SMY-75
//
// Гейт беты: «менее 10% обращений в поддержку связаны с непониманием лимитов».
// Достигается он не объяснениями, а тем, что человек может посмотреть, за что
// именно списано, — поэтому таблица показывает и то, что НЕ списалось:
// бесплатные правки, возвраты, начисления пакетом.
//
// Цвет полосы решает сервер полем `low`: порог живёт в конфигурации, и второе
// число здесь означало бы второй источник правды.

import { useEffect, useState } from 'react'
import { pluralize, REASON_LABELS, UNIT_LABELS, UNIT_PLURALS, type LedgerEvent, type UnitId } from '@smyslika/core'
import type { PackOfferView } from '@smyslika/api-client'

import { dayLabel, rub, whenLabel } from '@smyslika/core'
import { usePurchase } from '../purchase.tsx'
import { useSession } from '../session.tsx'
import { Shell } from '../shell/Shell.tsx'
import { RESULT_TITLES } from '@smyslika/ui-tokens'
import { Caps, ProgressBar } from '../ui/index.ts'

/**
 * Итог строки журнала — и словом, и цветом.
 *
 * «Бесплатно» здесь так же важно, как «списано»: правка в окне и отмена до
 * старта — это обещания продукта, и увидеть их выполненными человек должен в
 * том же списке, где видит списания.
 */
function outcomeOf(e: LedgerEvent): { label: string; kind: 'charged' | 'free' | 'added' } {
  if (e.delta > 0) return { label: REASON_LABELS[e.reason], kind: 'added' }
  if (e.delta === 0) return { label: 'Бесплатно', kind: 'free' }
  return { label: 'Списано', kind: 'charged' }
}

export function Limits() {
  const { balances, billing, me, reference, refreshBalances, tariff, serverNow } = useSession()
  const [events, setEvents] = useState<LedgerEvent[] | null>(null)
  const [offers, setOffers] = useState<PackOfferView[]>([])
  // Докупка — тем же путём, что на экране тарифов: у гостя через аккаунт по
  // почте (SMY-341, purchase.tsx).
  const purchase = usePurchase(() => void refreshBalances())

  useEffect(() => {
    let alive = true
    void billing.ledger().then((r) => alive && setEvents(r.events)).catch(() => alive && setEvents([]))
    void billing.packs().then((p) => alive && setOffers(p.offers)).catch(() => {})
    return () => {
      alive = false
    }
    // Человек сменился — гость вошёл в существующий аккаунт из окна перед
    // оплатой (SMY-341): журнал у того аккаунта свой.
  }, [billing, me?.userId])

  const now = serverNow()
  const current = reference?.tariffs.find((t) => t.id === tariff)
  const granted = balances?.source === 'grant'

  return (
    <Shell account>
      <div className="content account">
        <h1 className="account__title">Лимиты и списания</h1>

        <div className="limits">
          <div className="limits__tariff">
            <Caps>Текущий тариф</Caps>
            <span className="limits__name">{current?.label ?? '—'}</span>
            <span className="limits__price">
              {current == null || current.price === 0 ? 'бесплатный' : `${rub(current.price)} / мес`}
            </span>
            <span style={{ flex: 1 }} />
            {/* У выданного тарифа даты списания нет вовсе, и сочинять её
                нельзя: строка «продлится 3 октября» на подарке — это намёк на
                списание, которого не будет. */}
            <span className="limits__foot">
              {/* «Остатки не переносятся» — про лимит тарифа, и с появлением
                  бессрочных пакетов эту границу приходится называть вслух:
                  иначе фраза читается как отмена того, за что заплатили. */}
              {balances?.until != null
                ? `Лимиты обнулятся ${dayLabel(balances.until)} — остатки тарифа не переносятся.`
                : 'Лимиты обнуляются в день продления — остатки тарифа не переносятся.'}
              {/* «Пакеты», а не «купленные пакеты»: остаток пакетов приходит одним
                  числом на купленные и начисленные поддержкой, и подарок не
                  должен выглядеть купленным (SMY-64, SMY-325). Сторож —
                  scripts/check-packs-wording.mjs. */}
              {' Пакеты не сгорают.'}
              {granted ? ' Тариф выдан на время беты.' : ''}
            </span>
          </div>

          <div className="limits__bars">
            {(balances?.balances ?? []).map((b) => {
              // Докупленное считается отдельно от месячного. `remaining`
              // приходит уже с пакетами, поэтому месячный остаток —
              // разность, а не второе число с сервера.
              const fromPacks = b.fromPacks ?? 0
              const monthly = b.remaining - fromPacks
              return (
                <div key={b.unit} className="limits__bar">
                  <span className="limits__bar-title">
                    {UNIT_PLURALS[b.unit].charAt(0).toUpperCase() + UNIT_PLURALS[b.unit].slice(1)}
                  </span>
                  {/* «N из M» — про МЕСЯЦ, и полоса тоже. Складывать сюда
                      пакет нельзя: показав «50 из 30», экран обещал бы, что
                      всё это сгорит первого числа, — а пакет не сгорает. */}
                  <span className={`limits__bar-value${b.low ? ' limits__bar-value--warn' : ''}`}>
                    {monthly} из {b.limit}
                    {fromPacks > 0 ? <span className="limits__bar-extra"> + {fromPacks} из пакета</span> : null}
                  </span>
                  <ProgressBar value={monthly} max={b.limit} low={b.low} />
                  {/* Итог строкой под полосой: человек, купивший пакет,
                      спрашивает «сколько у меня всего», и ответ на это не
                      должен требовать сложения в уме. */}
                  {fromPacks > 0 ? (
                    <span className="limits__bar-total">
                      Всего доступно: {b.remaining} {UNIT_PLURALS[b.unit]} — пакет не сгорает в конце месяца
                    </span>
                  ) : null}
                </div>
              )
            })}
            {balances == null ? <p className="muted">Остатки ещё едут.</p> : null}
          </div>
        </div>

        {/* Пакет докупается на любом тарифе и любой из трёх — экран стоит
            прямо под полосами лимитов, и кончившаяся полоса обязана иметь
            рядом свою кнопку, а не чужую. */}
        {offers.length > 0 ? (
          <div className="packs">
            {offers.map((pack) => (
              <div key={pack.unit} className="pack">
                <span className="pack__body">
                  {/* Родительный падеж: «Пакет вопросов», а не «Пакет вопросы».
                      UNIT_PLURALS даёт именительный — он для подписи к чекбоксу
                      «больше не спрашивать про документы», и здесь не годится. */}
                  <span className="pack__title">
                    Пакет {UNIT_LABELS[pack.unit].many} +{pack.amount}
                  </span>
                  <span className="pack__note">{pack.note}</span>
                </span>
                <span className="pack__price">{rub(pack.price)}</span>
                <button
                  type="button"
                  className="btn btn--accent"
                  disabled={purchase.busy}
                  onClick={() => void purchase.pay({ kind: 'pack', unit: pack.unit })}
                >
                  Докупить
                </button>
              </div>
            ))}
          </div>
        ) : null}

        {purchase.note != null ? (
          <p className="account__note" role="status">
            {purchase.note}
          </p>
        ) : null}

        <Caps>История списаний</Caps>

        {events == null ? (
          <p className="muted account__empty">Смотрю журнал…</p>
        ) : events.length === 0 ? (
          <p className="muted account__empty">
            Пока с лимита ничего не списывалось. Здесь будет видно каждое списание — и каждый случай, когда его не было.
          </p>
        ) : (
          <div className="table">
            <div className="table__head">
              <span>Задача</span>
              <span>Когда</span>
              <span>Единица</span>
              <span>Итог</span>
            </div>
            {events.map((e) => {
              const outcome = outcomeOf(e)
              return (
                <div key={e.id} className="table__row table__row--static">
                  <span className="table__cell table__cell--main">
                    {e.scenario != null ? (RESULT_TITLES[e.scenario] ?? e.scenario) : REASON_LABELS[e.reason]}
                    {e.fromPack === true ? <span className="table__tag">из пакета</span> : null}
                  </span>
                  <span className="table__cell table__cell--meta">{whenLabel(e.at, now)}</span>
                  <span className="table__cell">
                    {e.delta === 0
                      ? 'правка'
                      : `${Math.abs(e.delta)} ${pluralize(Math.abs(e.delta), UNIT_LABELS[e.unit as UnitId])}`}
                  </span>
                  <span className={`table__cell table__cell--${outcome.kind}`}>{outcome.label}</span>
                </div>
              )
            })}
          </div>
        )}

        {/* Правила списания приезжают с сервера, а не лежат в вёрстке: их
            показывает и шит подтверждения, и этот экран, и расходиться этим
            двум местам нельзя. */}
        {reference != null ? (
          <div className="rules">
            <Caps>Что не списывается с лимита</Caps>
            <ul className="rules__list">
              {reference.notCharged.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
            {/* Ссылка стоит ЗДЕСЬ, а не только в разделе «Документы»: правила
                списания читают там, где увидели остаток и не поняли списание,
                — а искать их в договоре человек не пойдёт (SMY-11). Список
                выше отвечает на «что не списывается», документ — на «а
                почему» и «что если я не согласен». */}
            <p className="rules__more">
              <a href="/legal/units" target="_blank" rel="noopener">
                Правила списания единиц
              </a>
            </p>
          </div>
        ) : null}
      </div>
      {purchase.sheet}
    </Shell>
  )
}
